import {
  Timestamp,
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../firebase/config";

const EVENTS_COLLECTION = "events";

const normalizeEventPayload = (payload) => ({
  titleHindi: payload.titleHindi,
  titleEnglish: payload.titleEnglish,
  descriptionHindi: payload.descriptionHindi,
  descriptionEnglish: payload.descriptionEnglish,
  eventDate:
    payload.eventDate instanceof Date
      ? Timestamp.fromDate(payload.eventDate)
      : Timestamp.fromDate(new Date(payload.eventDate)),
  createdAt: serverTimestamp(),
  images: [],
});

const newImageRecord = ({
  url,
  storagePath,
  captionHindi,
  captionEnglish,
}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  url,
  storagePath,
  captionHindi,
  captionEnglish,
  uploadedAt: new Date().toISOString(),
});

export const subscribeToEvents = (onChange) => {
  if (!db) {
    onChange([]);
    return () => {};
  }
  const eventsQuery = query(
    collection(db, EVENTS_COLLECTION),
    orderBy("eventDate", "asc"),
  );
  return onSnapshot(eventsQuery, (snapshot) => {
    const events = snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));
    onChange(events);
  });
};

export const createEvent = async (payload) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }
  const docRef = await addDoc(
    collection(db, EVENTS_COLLECTION),
    normalizeEventPayload(payload),
  );
  return docRef.id;
};

export const updateEvent = async (id, updates) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }
  const eventRef = doc(db, EVENTS_COLLECTION, id);
  const payload = { ...updates };
  if (payload.eventDate) {
    payload.eventDate =
      payload.eventDate instanceof Date
        ? Timestamp.fromDate(payload.eventDate)
        : Timestamp.fromDate(new Date(payload.eventDate));
  }
  delete payload.id;
  await updateDoc(eventRef, payload);
};

export const addEventImage = async (
  eventId,
  { file, captionHindi, captionEnglish },
) => {
  if (!db || !storage) {
    throw new Error("Firebase storage is not configured.");
  }
  const storagePath = `events/${eventId}/${Date.now()}_${file.name}`;
  const fileRef = ref(storage, storagePath);
  const uploadResult = await uploadBytes(fileRef, file);
  const downloadUrl = await getDownloadURL(uploadResult.ref);

  const imagePayload = newImageRecord({
    url: downloadUrl,
    storagePath,
    captionHindi,
    captionEnglish,
  });
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  await updateDoc(eventRef, { images: arrayUnion(imagePayload) });
  return imagePayload;
};

export const removeEventImage = async (eventId, imageId) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const snapshot = await getDoc(eventRef);
  if (!snapshot.exists()) {
    return;
  }
  const event = snapshot.data();
  const images = event.images || [];
  const imageToRemove = images.find((item) => item.id === imageId);
  if (!imageToRemove) {
    return;
  }
  const filteredImages = images.filter((item) => item.id !== imageId);
  await updateDoc(eventRef, { images: filteredImages });
  if (imageToRemove.storagePath) {
    if (!storage) {
      throw new Error("Firebase storage is not configured.");
    }
    await deleteObject(ref(storage, imageToRemove.storagePath));
  }
};

export const deleteEvent = async (eventId) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const snapshot = await getDoc(eventRef);
  if (snapshot.exists()) {
    const event = snapshot.data();
    const images = event.images || [];
    if (storage) {
      await Promise.all(
        images
          .filter((item) => item.storagePath)
          .map((item) =>
            deleteObject(ref(storage, item.storagePath)).catch(() => undefined),
          ),
      );
    }
  }
  await deleteDoc(eventRef);
};
